import type { RefinementCtx } from "zod";
import { z } from "zod";

/** Assignee fields on a create-task step config. */
export type CreateTaskAssigneeConfig = {
  assigneeType?: "user" | "agent";
  assigneeUserId?: string;
  assigneeUserGroupIds?: string[];
  assigneeContactIds?: string[];
  assigneeAgentDefinitionId?: string;
};

function hasIds(ids: string[] | undefined): boolean {
  return (ids?.length ?? 0) > 0;
}

function userAssigneeIsMissing(value: CreateTaskAssigneeConfig): boolean {
  return (
    value.assigneeType === "user" &&
    !value.assigneeUserId &&
    !hasIds(value.assigneeUserGroupIds) &&
    !hasIds(value.assigneeContactIds)
  );
}

function agentAssigneeIsMissing(value: CreateTaskAssigneeConfig): boolean {
  return value.assigneeType === "agent" && !value.assigneeAgentDefinitionId;
}

function assigneeTypeIsMissing(value: CreateTaskAssigneeConfig): boolean {
  return (
    !value.assigneeType &&
    Boolean(
      value.assigneeUserId ||
      value.assigneeAgentDefinitionId ||
      hasIds(value.assigneeUserGroupIds) ||
      hasIds(value.assigneeContactIds),
    )
  );
}

/**
 * A configured assignee must name both a type and an id. Groups and contacts
 * count as a user assignee because the handler resolves them to a user.
 */
export function refineCreateTaskAssignee(
  value: CreateTaskAssigneeConfig,
  ctx: RefinementCtx,
): void {
  if (userAssigneeIsMissing(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeUserId"],
      message:
        "A user, user group, or contact is required when assigneeType is 'user'",
    });
  }
  if (agentAssigneeIsMissing(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeAgentDefinitionId"],
      message:
        "assigneeAgentDefinitionId is required when assigneeType is 'agent'",
    });
  }
  if (assigneeTypeIsMissing(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeType"],
      message:
        "assigneeType is required when a default assignee ID is provided",
    });
  }
}
